import { useEffect, useRef, useState } from 'react';
import './App.css';
import Hero from './components/Hero';
import QuoteSection from './components/QuoteSection';
import Skills from './components/Skills';
import Projects from './components/Projects';
import Experience from './components/Experience';
import Contact from './components/Contact';
import { LOCALES } from './utils/localize';

function App() {
  const [isReloading, setIsReloading] = useState(false);
  const [locale, setLocale] = useState(LOCALES.KR);
  const reloadTimeoutRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const savedLocale = window.localStorage.getItem('portfolio:locale');

    if (savedLocale === LOCALES.KR || savedLocale === LOCALES.EN) {
      setLocale(savedLocale);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem('portfolio:locale', locale);
  }, [locale]);

  useEffect(() => {
    const resizeObserverLoopMessages = [
      'ResizeObserver loop completed with undelivered notifications.',
      'ResizeObserver loop limit exceeded',
    ];

    const isResizeObserverLoopError = (message) => (
      typeof message === 'string'
      && resizeObserverLoopMessages.some((resizeObserverMessage) => message.includes(resizeObserverMessage))
    );

    const handleResizeObserverLoopError = (event) => {
      if (!isResizeObserverLoopError(event.message)) {
        return;
      }

      event.stopImmediatePropagation();
      event.preventDefault();
    };

    const handleResizeObserverLoopRejection = (event) => {
      if (!isResizeObserverLoopError(event.reason?.message ?? event.reason)) {
        return;
      }

      event.stopImmediatePropagation();
      event.preventDefault();
    };

    window.addEventListener('error', handleResizeObserverLoopError, true);
    window.addEventListener('unhandledrejection', handleResizeObserverLoopRejection, true);

    return () => {
      window.removeEventListener('error', handleResizeObserverLoopError, true);
      window.removeEventListener('unhandledrejection', handleResizeObserverLoopRejection, true);
    };
  }, []);

  useEffect(() => (
    () => {
      window.clearTimeout(reloadTimeoutRef.current);
    }
  ), []);

  const handleReloadPortfolio = () => {
    window.clearTimeout(reloadTimeoutRef.current);
    setIsReloading(true);
    window.dispatchEvent(new CustomEvent('portfolio:reload'));
    window.scrollTo({ top: 0, behavior: 'smooth' });

    reloadTimeoutRef.current = window.setTimeout(() => {
      setIsReloading(false);
    }, 1280);
  };

  return (
    <div className={`App ${isReloading ? 'is-reloading' : ''}`}>
      <div className="portfolio-topbar">
        <div className="section-shell portfolio-topbar-inner">
          <button type="button" className="portfolio-reload" onClick={handleReloadPortfolio} aria-label="Reload portfolio">
            <span className="portfolio-reload-core">
              <span className="portfolio-reload-type">Portfolio</span>
              <span className="portfolio-reload-scope">::</span>
              <span className="portfolio-reload-method">reload</span>
              <span className="portfolio-reload-paren">();</span>
            </span>
            <span className="portfolio-reload-reveal" aria-hidden="true">
              <span className="portfolio-reload-action">syncSections</span>
              <span className="portfolio-reload-paren">();</span>
            </span>
          </button>
          <div className="portfolio-locale-toggle" role="group" aria-label="Language selector">
            <button
              type="button"
              className={`portfolio-locale-option ${locale === LOCALES.KR ? 'is-active' : ''}`}
              onClick={() => setLocale(LOCALES.KR)}
              aria-pressed={locale === LOCALES.KR}
            >
              KR
            </button>
            <button
              type="button"
              className={`portfolio-locale-option ${locale === LOCALES.EN ? 'is-active' : ''}`}
              onClick={() => setLocale(LOCALES.EN)}
              aria-pressed={locale === LOCALES.EN}
            >
              EN
            </button>
          </div>
        </div>
      </div>
      <div className="portfolio-reload-scan" aria-hidden="true" />
      <div className="ambient-bubbles" aria-hidden="true">
        <span className="ambient-bubble bubble-1" />
        <span className="ambient-bubble bubble-2" />
        <span className="ambient-bubble bubble-3" />
        <span className="ambient-bubble bubble-4" />
        <span className="ambient-bubble bubble-5" />
        <span className="ambient-bubble bubble-6" />
      </div>
      <Hero locale={locale} />
      <QuoteSection locale={locale} />
      <Skills locale={locale} />
      <Projects locale={locale} />
      <Experience locale={locale} />
      <Contact locale={locale} />
      <footer className="site-copyright">
        <div className="section-shell">
          <p>© 2026. Park Hui Jun. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
